import { createSlice } from "@reduxjs/toolkit";

import { addWebSocketCases } from "../helpers/storeAction";
import {
  addNewBillingCodeData,
  deleteBillingCode,
  fetchBillingCodeListData,
  updateBillingCode,
} from "../thunks/accountingThunk";

import type { Bills } from "../../utils/types/accounting";

interface BillingCodeListState {
  billingCodeList: Bills[];
  loading: boolean;
  error: string | null;
  isDataFetched?: boolean;
}

const initialState: BillingCodeListState = {
  billingCodeList: [],
  loading: false,
  error: null,
  isDataFetched: false,
};

export const billingCodeListSlice = createSlice({
  name: "billingCodeList",
  initialState,
  reducers: {
    resetDataFetched: (state) => {
      state.isDataFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBillingCodeListData.pending, (state) => {
        state.loading = true;
        state.isDataFetched = true;
      })
      .addCase(fetchBillingCodeListData.fulfilled, (state, action) => {
        state.billingCodeList = action.payload || [];
        state.loading = false;
      })
      .addCase(fetchBillingCodeListData.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isDataFetched = false;
        state.loading = false;
      });

    builder
      .addCase(updateBillingCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBillingCode.fulfilled, (state, action) => {
        state.loading = false;
        state.billingCodeList = state.billingCodeList.map((bill) => {
          if (bill.accountingId === action.payload?.accountingId) {
            return { ...bill, ...action.payload };
          }
          return bill;
        });
      })
      .addCase(updateBillingCode.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(deleteBillingCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBillingCode.fulfilled, (state, action) => {
        state.loading = false;
        state.billingCodeList = state.billingCodeList.filter(
          (bill) => bill.accountingId !== action.payload?.id
        );
      })
      .addCase(deleteBillingCode.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(addNewBillingCodeData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewBillingCodeData.fulfilled, (state, action) => {
        state.loading = false;
        state.billingCodeList = [action.payload, ...state.billingCodeList];
      })
      .addCase(addNewBillingCodeData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    addWebSocketCases(builder, "billingCodeList", "billingCodeList");
  },
});

export const { resetDataFetched } = billingCodeListSlice.actions;
export default billingCodeListSlice.reducer;
